import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Menu, X } from "lucide-react";
import { MAPS_DIRECTIONS_URL } from "@/lib/info";

const LINKS = [
  { label: "Menu", href: "#menu" },
  { label: "Jell-O Bar", href: "#jello" },
  { label: "Games", href: "#games" },
  { label: "Jukebox", href: "#jukebox" },
  { label: "Reviews", href: "#reviews" },
  { label: "Location", href: "#location" },
];

const Brand = () => (
  <a
    data-testid="navbar-logo"
    href="#top"
    className="font-display text-3xl md:text-4xl tracking-wide text-[#F5F2E6]"
  >
    SUNNY<span className="text-[#FF2A2A]">'</span>S
  </a>
);

const DesktopNav = () => (
  <nav className="hidden md:flex items-center gap-8">
    {LINKS.map((l) => (
      <a
        key={l.label}
        href={l.href}
        data-testid={`nav-link-${l.label.toLowerCase()}`}
        className="font-mono-plex text-xs uppercase tracking-[0.18em] text-[#A3A3A3] hover:text-[#F5F2E6] transition-colors"
      >
        {l.label}
      </a>
    ))}
  </nav>
);

const DirectionsCTA = ({ testid, className }) => (
  <a
    href={MAPS_DIRECTIONS_URL}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className={`btn-press inline-flex items-center gap-2 bg-[#FF2A2A] text-white px-4 py-2.5 rounded-sm font-mono-plex text-xs uppercase tracking-[0.2em] hover:bg-[#ff4444] ${
      className || ""
    }`}
  >
    <MapPin size={14} strokeWidth={2.5} />
    Get Directions
  </a>
);

const MobileToggle = ({ open, onClick }) => (
  <button
    data-testid="navbar-mobile-toggle"
    onClick={onClick}
    className="md:hidden p-2 text-[#F5F2E6]"
    aria-label="Toggle menu"
  >
    {open ? <X size={22} /> : <Menu size={22} />}
  </button>
);

const MobileMenu = ({ onClose }) => (
  <div
    data-testid="navbar-mobile-menu"
    className="md:hidden bg-black/95 border-t border-white/10 px-6 py-6 flex flex-col gap-5"
  >
    {LINKS.map((l) => (
      <a
        key={l.label}
        href={l.href}
        onClick={onClose}
        className="font-display text-2xl tracking-wide text-[#F5F2E6]"
      >
        {l.label}
      </a>
    ))}
    <a
      href={MAPS_DIRECTIONS_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="navbar-mobile-cta"
      className="mt-2 inline-flex items-center justify-center gap-2 bg-[#FF2A2A] text-white px-4 py-3 rounded-sm font-mono-plex text-xs uppercase tracking-[0.2em]"
    >
      <MapPin size={14} strokeWidth={2.5} />
      Get Directions
    </a>
  </div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 8);
  }, [setScrolled]);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const headerClass = scrolled
    ? "bg-black/85 backdrop-blur-md border-b border-white/10"
    : "bg-transparent border-b border-transparent";

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-colors duration-300 ${headerClass}`}
    >
      <div className="px-6 md:px-12 lg:px-24 h-16 md:h-20 flex items-center justify-between">
        <Brand />
        <DesktopNav />
        <DirectionsCTA
          testid="navbar-cta-directions"
          className="hidden md:inline-flex"
        />
        <MobileToggle open={open} onClick={() => setOpen((v) => !v)} />
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
};

export default Navbar;
